import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Loader2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  useUninvoicedAppointments,
  useAddAppointmentInvoiceLine,
  type UninvoicedAppointment,
} from '@/hooks/useAppointmentInvoicing';
import { formatMoney } from '@/hooks/useInvoices';

interface AppointmentLinePickerProps {
  invoiceId: string;
  patientId: string;
  clinicId: string;
  currency: string;
  onClose: () => void;
}

function formatAppointmentWhen(startsAt: string): string {
  const date = new Date(startsAt);
  return `${format(date, 'dd. MMM yyyy', { locale: da })} · ${format(date, 'HH:mm')}`;
}

export function AppointmentLinePicker({
  invoiceId,
  patientId,
  clinicId,
  currency,
  onClose,
}: AppointmentLinePickerProps) {
  const { t } = useTranslation();
  const appointments = useUninvoicedAppointments(patientId, clinicId);
  const addAppointmentLine = useAddAppointmentInvoiceLine();

  const handleSelect = async (appt: UninvoicedAppointment) => {
    try {
      await addAppointmentLine.mutateAsync({
        invoice_id: invoiceId,
        appointment_id: appt.id,
        patient_id: patientId,
        clinic_id: clinicId,
      });
      toast.success(t('invoices.appointments.added'));
      onClose();
    } catch (err) {
      console.error('add appointment line failed', err);
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('already_invoiced')) {
        toast.error(t('invoices.appointments.errors.already_invoiced'));
      } else {
        toast.error(t('invoices.save_error'));
      }
    }
  };

  return (
    <Modal title={t('invoices.appointments.title')} onClose={onClose}>
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        {t('invoices.appointments.hint')}
      </p>

      {appointments.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : !appointments.data?.length ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
          {t('invoices.appointments.empty')}
        </p>
      ) : (
        <ul className="max-h-80 space-y-2 overflow-y-auto">
          {appointments.data.map((appt) => (
            <li
              key={appt.id}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {appt.service_name}
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatAppointmentWhen(appt.starts_at)}
                  </span>
                  {appt.clinician_name && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {appt.clinician_name}
                    </span>
                  )}
                </p>
              </div>
              <span className="shrink-0 text-sm font-medium text-gray-900 dark:text-white">
                {formatMoney(appt.price_cents, currency)}
              </span>
              <Button
                type="button"
                size="sm"
                disabled={addAppointmentLine.isPending}
                onClick={() => void handleSelect(appt)}
              >
                <Plus className="h-4 w-4" />
                {t('invoices.appointments.add')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
